import { useEffect } from 'react';
import { useAuthStore } from '../stores/authStore';
import { setDiagnosticOwner, startBrowserDiagnostics, stopBrowserDiagnostics } from '../lib/infra/browserDiagnostics';

export function useBrowserDiagnostics() {
  useEffect(() => {
    let checked: string | null = null;
    let probe: AbortController | null = null;
    const sync = (s: ReturnType<typeof useAuthStore.getState>) => {
      if (s.status === 'unauthenticated') {
        probe?.abort(); checked = null; setDiagnosticOwner(null);
      }
      else if (s.user?.id) setDiagnosticOwner(s.user.id);
      if (s.status !== 'authenticated' || !s.user?.id || !s.token || checked === s.user.id) return;
      probe?.abort();
      const id = s.user.id;
      checked = id;
      const controller = new AbortController(); probe = controller;
      const timeout = setTimeout(() => controller.abort(), 5_000);
      void fetch('/api/diagnostics/browser', {
        headers: { Authorization: `Bearer ${s.token}` }, signal: controller.signal,
      }).then(async (response) => {
        if (!response.ok) return;
        const config = await response.json();
        const auth = useAuthStore.getState();
        if (controller.signal.aborted || auth.status !== 'authenticated' || auth.user?.id !== id
          || config.automatic !== true || !(config.remainingMs === null
            || (Number.isFinite(config.remainingMs) && config.remainingMs > 0))) return;
        startBrowserDiagnostics(async (sample, signal) => {
          const current = useAuthStore.getState();
          if (current.status !== 'authenticated' || current.user?.id !== id || !current.token) return false;
          const result = await fetch('/api/diagnostics/browser', { method: 'POST', signal,
            headers: { Authorization: `Bearer ${current.token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify(sample) });
          if (result.status === 401 || result.status === 404) stopBrowserDiagnostics();
          return result.ok;
        }, config.remainingMs ?? Infinity, true);
      }).catch(() => { /* Unavailable diagnostics never affects the application. */ })
        .finally(() => clearTimeout(timeout));
    };
    sync(useAuthStore.getState());
    const unsubscribe = useAuthStore.subscribe(sync);
    return () => { probe?.abort(); unsubscribe(); stopBrowserDiagnostics(); };
  }, []);
}
